<template>
  <div class="gp-page" data-testid="capability-growth-shell">
    <div class="gp-header">
      <div>
        <h1 class="gp-title">Growth</h1>
        <p class="gp-subtitle">
          Growth progress organized by capability: what you communicate and campaign for,
          and what it earns. The full picture below shows every channel — nothing here is
          ever fabricated; unconnected sources say so.
        </p>
      </div>
    </div>

    <!-- Site traffic + behaviour moved to Site Settings (the Overview); Growth
         stays focused on channels + capability progress. -->

    <!-- ── Full picture: site-wide channels (existing growth-dashboard envelope) ── -->
    <section class="gp-section" data-testid="gp-full-picture">
      <h2 class="gp-section-title">Full picture</h2>
      <div v-if="store.overviewLoading" class="gp-muted">Loading channels…</div>
      <p v-else-if="store.overviewError" class="gp-muted" data-testid="gp-overview-error">
        Channel overview unavailable right now — capability progress below still works.
      </p>
      <div v-else-if="store.overview" class="gp-channel-grid">
        <div
          v-for="card in channelCards"
          :key="card.key"
          class="gp-channel-card"
          :data-status="card.status"
          data-testid="gp-channel-card"
        >
          <div class="gp-channel-head">
            <span class="gp-channel-label">{{ card.label }}</span>
            <span class="gp-pill" :data-status="card.status">{{ STATUS_LABELS[card.status] || card.status }}</span>
          </div>
          <div v-if="card.status === 'ok'" class="gp-channel-metrics">
            <div v-for="metric in card.metrics" :key="metric.key" class="gp-metric">
              <span class="gp-metric-value">{{ metric.formatted }}</span>
              <span class="gp-metric-label">{{ metric.label }}</span>
            </div>
          </div>
          <p v-else class="gp-channel-next" data-testid="gp-channel-next-step">
            {{ card.next_step || 'Connect this source to light up the channel.' }}
          </p>
        </div>

        <!-- Social engagement channel comes from the collector, not the envelope -->
        <div class="gp-channel-card" :data-status="store.social?.status || 'empty'" data-testid="gp-channel-card">
          <div class="gp-channel-head">
            <span class="gp-channel-label">Social engagement</span>
            <span class="gp-pill" :data-status="store.social?.status || 'empty'">
              {{ STATUS_LABELS[store.social?.status] || 'No data' }}
            </span>
          </div>
          <div v-if="store.social?.status === 'ok'" class="gp-channel-metrics">
            <div v-for="metric in socialMetrics" :key="metric.key" class="gp-metric">
              <span class="gp-metric-value">{{ metric.formatted }}<span v-if="metric.delta" class="gp-delta" :data-up="metric.delta > 0">{{ metric.delta > 0 ? '▲' : '▼' }}{{ Math.abs(metric.delta).toLocaleString() }}</span></span>
              <span class="gp-metric-label">{{ metric.label }}</span>
            </div>
          </div>
          <p v-else class="gp-channel-next">
            No engagement measured yet — publish campaigns and run "Collect results" on the Capability Map.
          </p>
        </div>
      </div>
    </section>

    <!-- ── Capability progress ── -->
    <section class="gp-section">
      <div class="gp-section-head">
        <h2 class="gp-section-title">Capability progress</h2>
        <div class="gp-summary" data-testid="gp-summary">
          <span class="gp-chip" data-coverage="covered">{{ store.summary.covered }} covered</span>
          <span class="gp-chip" data-coverage="partial">{{ store.summary.partial }} partial</span>
          <span class="gp-chip" data-coverage="uncovered">{{ store.summary.uncovered }} uncovered</span>
        </div>
      </div>

      <div v-if="store.uiState === 'loading'" class="gp-state" data-testid="gp-state-loading">Loading growth progress…</div>
      <div v-else-if="store.uiState === 'error'" class="gp-state gp-state-error" data-testid="gp-state-error">
        <p>{{ store.error }}</p>
        <button type="button" class="gp-btn" @click="store.loadAll(siteId)">Retry</button>
      </div>
      <div v-else-if="store.uiState === 'empty'" class="gp-state" data-testid="gp-state-empty">
        <p>No capabilities synced yet.</p>
        <router-link :to="`/sites/${siteId}/capabilities`" class="gp-link">Open the Capability Map to sync from Feature1 →</router-link>
      </div>

      <div v-else class="gp-cap-list" data-testid="gp-state-ready">
        <article
          v-for="cap in store.rankedCapabilities"
          :key="cap.id"
          class="gp-cap-row"
          data-testid="gp-cap-row"
        >
          <div class="gp-cap-main">
            <div class="gp-cap-name-row">
              <span class="gp-cap-name">{{ cap.capability_name }}</span>
              <span class="gp-badge" :data-coverage="cap.coverage">{{ cap.coverage }}</span>
            </div>
            <span class="gp-cap-context">{{ cap.persona_name }} / {{ cap.module_name }}</span>
            <span class="gp-cap-counts">
              {{ cap.mention_count }} mentions · {{ cap.post_count + cap.campaign_count }} campaigns ·
              {{ cap.engagement.measured_posts }} measured
            </span>
          </div>

          <div v-if="cap.engagement.status === 'ok'" class="gp-cap-engagement">
            <div class="gp-cap-numbers">
              <div v-for="metric in capMetrics(cap)" :key="metric.key" class="gp-metric">
                <span class="gp-metric-value">{{ metric.formatted }}<span v-if="metric.delta" class="gp-delta" :data-up="metric.delta > 0">{{ metric.delta > 0 ? '▲' : '▼' }}{{ Math.abs(metric.delta).toLocaleString() }}</span></span>
                <span class="gp-metric-label">{{ metric.label }}</span>
              </div>
            </div>
            <svg
              v-if="sparkPoints(cap).length > 1"
              class="gp-spark"
              viewBox="0 0 100 28"
              preserveAspectRatio="none"
              data-testid="gp-sparkline"
              aria-hidden="true"
            >
              <polyline :points="sparkPath(cap)" fill="none" stroke="currentColor" stroke-width="2" />
            </svg>
          </div>
          <p v-else class="gp-cap-unmeasured" data-testid="gp-cap-unmeasured">
            Not measured yet{{ cap.coverage === 'uncovered' ? ' — no campaigns exist for this capability' : '' }}.
          </p>
        </article>
      </div>
    </section>

    <span class="gp-sr-only" aria-live="polite">{{ store.announce }}</span>
  </div>
</template>

<script setup>
// Capability growth progress (capability growth integration).
//
// Two halves, independently loaded: the site-wide channel picture reuses the
// existing growth-dashboard envelope (status vocabulary preserved — channels
// without a connected source say so, no fabricated numbers), and capability
// progress reads the engagement history the results collector accumulates.

import { computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useGrowthProgressStore } from '../stores/growthProgress'

const props = defineProps({ id: { type: String, required: true } })
const route = useRoute()
const store = useGrowthProgressStore()

const siteId = computed(() => props.id || route.params.id).value

const STATUS_LABELS = {
  ok: 'Live',
  empty: 'No data yet',
  unavailable: 'Not connected',
  error: 'Failing',
}

const CHANNEL_META = [
  { key: 'traffic', label: 'Search traffic', metrics: [['organic_clicks', 'Clicks'], ['organic_impressions', 'Impressions']] },
  { key: 'seo', label: 'SEO health', metrics: [['technical_seo', 'Technical'], ['aeo_score', 'AEO']] },
  { key: 'social_referral', label: 'Social referral', metrics: [['social_referral_sessions', 'Sessions']] },
  { key: 'lead_outcomes', label: 'Leads', metrics: [['total_leads', 'Total leads']] },
]

const fmt = (v) => (typeof v === 'number' && Number.isFinite(v) ? Number(v).toLocaleString() : '—')

const channelCards = computed(() => {
  const channels = store.overview?.channels || {}
  return CHANNEL_META.map((meta) => {
    const block = channels[meta.key] || { status: 'unavailable', data: null }
    return {
      key: meta.key,
      label: meta.label,
      status: block.status,
      next_step: block.next_step,
      metrics: meta.metrics.map(([key, label]) => ({ key, label, formatted: fmt(block.data?.[key]) })),
    }
  })
})

const ENGAGEMENT_METRICS = [['impressions', 'Impressions'], ['likes', 'Likes'], ['comments', 'Comments'], ['shares', 'Shares']]

const metricRows = (engagement) =>
  ENGAGEMENT_METRICS.map(([key, label]) => ({
    key,
    label,
    formatted: fmt(engagement?.totals?.[key]),
    delta: engagement?.delta?.[key] ?? null,
  })).filter((m) => m.formatted !== '—')

const socialMetrics = computed(() => metricRows(store.social))
const capMetrics = (cap) => metricRows(cap.engagement)

// Inline impressions sparkline from the per-day trend.
const sparkPoints = (cap) =>
  (cap.engagement?.trend || []).map((t) => t.impressions).filter((v) => typeof v === 'number')

const sparkPath = (cap) => {
  const points = sparkPoints(cap)
  const max = Math.max(...points, 1)
  const step = points.length > 1 ? 100 / (points.length - 1) : 100
  return points.map((v, i) => `${(i * step).toFixed(1)},${(26 - (v / max) * 24).toFixed(1)}`).join(' ')
}

onMounted(() => store.loadAll(siteId))
watch(() => route.params.id, (next, prev) => {
  if (next && next !== prev) store.loadAll(next)
})
</script>

<style scoped>
.gp-page { max-width: 1100px; margin: 0 auto; padding: 24px 16px 48px; }
.gp-title { font-size: 1.5rem; font-weight: 700; color: var(--text-primary); margin: 0 0 4px; }
.gp-subtitle { color: var(--text-secondary); font-size: 0.9rem; margin: 0; max-width: 680px; }
.gp-muted { color: var(--text-secondary); font-size: 0.85rem; }
.gp-link { color: var(--accent-light); font-size: 0.85rem; text-decoration: none; }
.gp-link:hover { text-decoration: underline; }
.gp-btn { background: var(--glass-bg); color: var(--text-primary); border: var(--glass-border); border-radius: var(--radius-sm); padding: 7px 14px; font-size: 0.875rem; cursor: pointer; transition: background 0.15s; }
.gp-btn:hover { background: var(--glass-bg-hover); }

.gp-section { margin-top: 28px; }
.gp-section-head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.gp-section-title { font-size: 1.05rem; font-weight: 700; color: var(--text-primary); margin: 0 0 12px; }

.gp-channel-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 12px; }
.gp-channel-card { background: var(--glass-bg); border: var(--glass-border); border-radius: var(--radius-md); padding: 14px; color: var(--text-primary); backdrop-filter: var(--glass-blur); -webkit-backdrop-filter: var(--glass-blur); box-shadow: var(--elev-sm); }
.gp-channel-card[data-status='ok'] { border-top: 3px solid var(--success); }
.gp-channel-head { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; margin-bottom: 10px; }
.gp-channel-label { font-weight: 600; color: var(--text-primary); font-size: 0.875rem; }
.gp-pill { font-size: 0.68rem; font-weight: 700; border-radius: 999px; padding: 2px 8px; background: var(--glass-bg-active); color: var(--text-secondary); white-space: nowrap; }
.gp-pill[data-status='ok'] { background: rgba(52,211,153,0.18); color: var(--success); }
.gp-pill[data-status='error'] { background: rgba(248,113,113,0.18); color: var(--danger); }
.gp-channel-metrics { display: flex; gap: 16px; flex-wrap: wrap; }
.gp-channel-next { color: var(--text-secondary); font-size: 0.78rem; margin: 0; }

.gp-metric { display: flex; flex-direction: column; }
.gp-metric-value { font-size: 1.05rem; font-weight: 700; color: var(--text-primary); }
.gp-metric-label { font-size: 0.68rem; color: var(--text-secondary); text-transform: uppercase; letter-spacing: 0.03em; }
.gp-delta { font-size: 0.7rem; font-weight: 700; margin-left: 5px; color: var(--danger); }
.gp-delta[data-up='true'] { color: var(--success); }

.gp-summary { display: flex; gap: 8px; }
.gp-chip { font-size: 0.8rem; font-weight: 600; border-radius: 999px; padding: 3px 10px; background: var(--glass-bg-active); color: var(--text-secondary); }
.gp-chip[data-coverage='covered'] { background: rgba(52,211,153,0.18); color: var(--success); }
.gp-chip[data-coverage='partial'] { background: rgba(251,191,36,0.18); color: var(--warning); }
.gp-chip[data-coverage='uncovered'] { background: rgba(248,113,113,0.18); color: var(--danger); }

.gp-state { text-align: center; color: var(--text-secondary); padding: 48px 16px; }
.gp-state-error p { color: var(--danger); margin-bottom: 12px; }

.gp-cap-list { display: flex; flex-direction: column; gap: 10px; }
.gp-cap-row { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; background: var(--glass-bg); border: var(--glass-border); border-radius: var(--radius-md); padding: 14px; color: var(--text-primary); backdrop-filter: var(--glass-blur); -webkit-backdrop-filter: var(--glass-blur); box-shadow: var(--elev-sm); }
.gp-cap-main { display: flex; flex-direction: column; gap: 2px; min-width: 220px; }
.gp-cap-name-row { display: flex; align-items: baseline; gap: 8px; }
.gp-cap-name { font-weight: 600; color: var(--text-primary); font-size: 0.95rem; }
.gp-badge { font-size: 0.68rem; font-weight: 700; border-radius: 999px; padding: 2px 8px; text-transform: capitalize; }
.gp-badge[data-coverage='covered'] { background: rgba(52,211,153,0.18); color: var(--success); }
.gp-badge[data-coverage='partial'] { background: rgba(251,191,36,0.18); color: var(--warning); }
.gp-badge[data-coverage='uncovered'] { background: rgba(248,113,113,0.18); color: var(--danger); }
.gp-cap-context { font-size: 0.75rem; color: var(--text-secondary); }
.gp-cap-counts { font-size: 0.78rem; color: var(--text-secondary); }
.gp-cap-engagement { display: flex; align-items: center; gap: 18px; flex-wrap: wrap; }
.gp-cap-numbers { display: flex; gap: 16px; }
.gp-cap-unmeasured { color: var(--text-secondary); font-size: 0.8rem; margin: 0; }
.gp-spark { width: 120px; height: 28px; color: var(--accent); }

.gp-sr-only {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0;
}
</style>
